import type {PartialOptions} from 'overlayscrollbars';

/**
 * Shared OverlayScrollbars presets. Every custom scrollbar of the app goes through
 * one of these, all with the Kabeen-flavoured `os-theme-dark` handle (global override
 * in `styles.scss`); only the auto-hide behaviour differs per surface.
 *
 * `os-theme-dark` on purpose: the built-in theme ships real handle styles that the
 * global override only re-colours. A custom theme name would leave the library's
 * `--os-handle-bg: none` default in place and the handle invisible.
 *
 * Expose a preset as a field to use it from a template:
 * `protected readonly drawerScrollOptions = DRAWER_SCROLL_OPTIONS;`
 */
const THEME = 'os-theme-dark';

// Both axes are set explicitly on the vertical presets: passing only one can make
// OverlayScrollbars fall back to `hidden` on the other and silently disable scrolling.
const VERTICAL_ONLY: PartialOptions['overflow'] = {x: 'hidden', y: 'scroll'};

/**
 * Right drawers and their inner panels (comments, …): the handle shows while
 * scrolling and fades out on its own once the scroll stops.
 */
export const DRAWER_SCROLL_OPTIONS: PartialOptions = {
	scrollbars: {autoHide: 'scroll', autoHideDelay: 800, theme: THEME},
	overflow: VERTICAL_ONLY,
};

/**
 * Side panels the pointer moves in and out of (main menu, global search results,
 * Prism): the handle is visible while the pointer is over the panel and fades once
 * it leaves.
 */
export const PANEL_SCROLL_OPTIONS: PartialOptions = {
	scrollbars: {autoHide: 'leave', autoHideDelay: 100, theme: THEME},
	overflow: VERTICAL_ONLY,
};

/**
 * Rows area of the list pages, both axes: always visible so users see there is
 * more to scroll.
 */
export const TABLE_SCROLL_OPTIONS: PartialOptions = {
	scrollbars: {autoHide: 'never', theme: THEME},
};

/** The routed page, both axes: some pages (org tree, discovery deck) are wider than the viewport. */
export const PAGE_SCROLL_OPTIONS: PartialOptions = {
	scrollbars: {autoHide: 'leave', autoHideDelay: 100, theme: THEME},
	overflow: {x: 'scroll', y: 'scroll'},
};
